/**
 * Every tunable number for bomb defusal, in one place.
 *
 * The user described "2 mins after the planting"; Counter-Strike uses ~40 s and
 * they chose CS-authentic. Those are different games: a 2-minute fuse inverts
 * the mode, because planting stops being a win condition and every round
 * becomes a long retake. If you want the literal reading, set `bombTimer: 120`
 * here and nothing else changes.
 */
export const BOMB_RULES = {
  freezeTime: 4,        // CS uses 15; shorter suits a bots-only match
  roundTime: 115,       // 1:55, CS 1.6
  bombTimer: 40,        // fuse once planted — this is the number that matters
  plantTime: 3.2,
  defuseTime: 5,        // with a kit
  defuseTimeNoKit: 10,
  defuseKit: true,      // the player always carries one, for now
  postRound: 4,         // seconds of "round over" before the next freeze
  /**
   * Best of seven: first to four, and at most seven rounds.
   *
   * It was first-to-eight (best of fifteen), which is the competitive CS
   * format and takes an hour. Seven is a sitting: long enough that one lost
   * round is recoverable, short enough to finish. Both numbers are stated
   * rather than derived, because `roundsToWin` alone leaves the cap implicit
   * and the offline mode never enforced one at all.
   */
  roundsToWin: 4,
  roundsPerMatch: 7,
  botsPerTeam: 4,
  explodeRadius: 14,
  explodeDamage: 400,
  // How close you must be to interact, and how far you may drift before the
  // hold cancels. Both are deliberately tight: walking off a defuse is a real
  // decision in CS.
  useRange: 1.8,
  cancelMove: 0.45,
};

/**
 * Where multiplayer deliberately differs from the single-player game.
 *
 * Both are multipliers on the values above rather than a second copy of them,
 * so a rule change still lands in one place — and so the difference between the
 * two games is legible as exactly these three numbers.
 *
 * The reasoning: against bots you are one competent player against predictable
 * opposition, and a round resolves quickly. Against people nobody walks into
 * the open, rounds are decided by position rather than by aim, and the same
 * clock runs out mid-standoff. Longer rounds and tougher players buy the time
 * that makes holding an angle worth doing.
 */
export const ONLINE_RULES = {
  roundTimeScale: 2,
  healthScale: 2,
  /**
   * A match is at most this many rounds, and one side takes it by winning a
   * majority of them.
   *
   * Best of seven, matching the offline mode. Online rounds run at twice the
   * clock, so this is already the longer game of the two. Four is a clinch —
   * 4-1 stops at round five rather than playing two dead ones — and because
   * seven is odd, a bomb match cannot be drawn. Deathmatch still can be, since
   * there the score, not the round count, decides it.
   */
  roundsPerMatch: 7,
  roundsToWin: 4,
  /**
   * Per-player health overrides, by callsign (compared case-insensitively).
   *
   * A deliberate, named exception rather than a general handicap system,
   * because that is what was asked for and a general system nobody asked for is
   * a general system nobody maintains. Anyone can type the name, so treat this
   * as a house rule among people who know each other, not as an entitlement —
   * there is no identity here to attach it to.
   */
  healthByName: {
    iryna: 1000,
  },
};

/**
 * Explosives, in one table because BOTH sides need them.
 *
 * Online the client decides where a rocket or grenade went off — it has the
 * physics for the flight and the bounce, and waiting a round trip to see your
 * own grenade explode would feel broken — but the SERVER decides what that
 * costs. So the blast radius and damage cannot live at the client call site any
 * more: a client that sent its own numbers could send any numbers.
 *
 * `maxTravel` and `los` are what make the claim checkable. A rocket flies
 * straight, so the server can require an unobstructed line from the shooter to
 * the point they claim it hit; a grenade bounces round corners, so it cannot,
 * and only the distance is worth testing. Neither stops a determined cheat
 * outright — a client could still claim a legal-looking point — but both stop
 * the trivial one, which is a detonation on top of someone across the map.
 */
export const EXPLOSIVES = {
  rpg: {
    radius: 6.5, damage: 165,
    maxTravel: 400, los: true,
    // The launcher's own cycle time, with slack. Anything faster is not a gun.
    cooldown: 1.2,
  },
  frag: {
    radius: 9.5, damage: 140,
    // A hard throw is about 11 m; the cap is generous for a bounce downhill.
    maxTravel: 60, los: false,
    cooldown: 0.8,
  },
};

/**
 * The flashbang, which is not an explosive: it does no damage at all.
 *
 * It needs the server for a different reason. Being blinded depends on where
 * YOU were looking when it went off, and only the server knows where everyone
 * was looking — so a client can neither decide it for others (it would be
 * asserting their state) nor for itself (it would simply never be blinded).
 * Online it was entirely local: nobody could blind anyone, and nobody could be
 * blinded.
 */
export const FLASH = {
  radius: 22,
  maxTravel: 60,
  cooldown: 0.8,
  /**
   * How much of the effect is owed to facing it. A bang behind you still
   * registers; one you are looking straight at takes the screen.
   */
  facingFloor: 0.35,
};

/**
 * Killstreaks, shared because online the SERVER decides who has earned one.
 *
 * They were entirely client-side: earned from a kill count this machine kept
 * for itself, and spent by this machine deciding what happened. Online that
 * makes an airstrike unauthorisable — the server had no way to tell an earned
 * one from a fabricated one, so nine 220-damage blasts at client-chosen
 * coordinates could not be accepted and killstreaks stayed cosmetic.
 *
 * A tier is earned once per match at a cumulative kill count, which is what the
 * offline game already did; matching it keeps one set of rules for both.
 */
export const STREAK_TIERS = [
  { at: 3, id: 'uav', name: 'UAV', sub: 'ENEMIES REVEALED' },
  { at: 5, id: 'strike', name: 'PRECISION AIRSTRIKE', sub: 'MARK A TARGET' },
  { at: 7, id: 'heli', name: 'ATTACK HELO', sub: 'GUNSHIP INBOUND' },
];

export const STREAK_RULES = {
  /** Seconds the UAV keeps the enemy team painted. */
  uavTime: 22,
  /** How far a player may mark a strike. Matches the client's marking ray. */
  strikeMark: 260,
  strikeDelay: 3.4,
  strikeBombs: 9,
  strikeSpacing: 7.5,
  strikeRadius: 8.5,
  strikeDamage: 220,
  /** Gap between bombs in the run. */
  strikeStagger: 0.14,
  heliTime: 26,
  heliAlt: 34,
  heliRadius: 46,
  /**
   * Gunship damage per second, and it is a lot.
   *
   * This used to read 34 while the code that applied it multiplied by ten —
   * `HELI_DPS * 0.09 * 10` per 0.09 s burst — so the real figure was always
   * 340 and the constant was documentation for a number nobody used. Stated
   * honestly here because the server now has to agree with the client about it,
   * and two files cannot agree about a value one of them is secretly scaling.
   *
   * At 340 against 200 health a gunship kills in about six tenths of a second
   * of held line of sight. That is the behaviour the offline game already had;
   * it is now merely visible, and tunable in one place.
   */
  heliDps: 340,
  /** How often it re-picks a target. A threat, not a sniper. */
  heliRetarget: 1.2,
  /** Orbit speed, radians per second. Both sides must circle identically. */
  heliOrbit: 0.36,
};

/** The maximum health a player with this callsign gets online. */
export function onlineMaxHealth(name) {
  const named = ONLINE_RULES.healthByName[String(name ?? '').trim().toLowerCase()];
  return named ?? 100 * ONLINE_RULES.healthScale;
}

export const DM_RULES = {
  botCount: 8,
  respawnDelay: 6,
  playerRespawn: 3,
  /**
   * A deathmatch that ENDS.
   *
   * It never did offline: bots respawned one-for-one forever and nothing
   * counted, so there was no match to win and nothing to put on the award
   * screens. Online it has always ended, because the server enforces a score
   * limit; this is the same idea with the numbers stated where both can read
   * them.
   *
   * Thirty kills against eight bots and a player is a fifteen-to-twenty minute
   * game — long enough that a lead means something, short enough to finish.
   * The clock is the backstop for a lobby that is not scoring: whoever is ahead
   * at zero takes it, and the awards run either way.
   */
  scoreLimit: 30,
  matchTime: 900,
  /** Seconds the award screens hold before the next match. Matches bomb mode. */
  intermission: 22,
};
